import {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {Box, Button, CircularProgress, Stack, Typography} from '@mui/joy';
import AttachFileRounded from '@mui/icons-material/AttachFileRounded';
import ImageViewer from '../../home_media_ui/ImageViewer';
import {rasterImage} from '../../home_media_ui/imageFormats';
import {LayoutContext} from '../../context';
import {applicationAttachmentAccess, downloadApplicationAttachment} from './applicationAttachmentAccess';

function urlName(value) {
  try {
    const url = new URL(value, window.location.origin);
    return (url.searchParams.get('key') || decodeURIComponent(url.pathname)).split('/').pop();
  } catch { return ''; }
}

function attachmentNodes(order) {
  const nodes = [];
  if (order.plan_file) nodes.push({id: 'plan', plan: true, kind: 'file',
    name: order.plan_file_name || urlName(order.plan_file) || 'План помещения',
    mime: order.plan_file_mime || '', version: order.date_update || order.plan_file});
  for (const file of order.input_files || []) {
    if (!file.id) continue;
    nodes.push({id: `file-${file.id}`, fileId: file.id, kind: 'file',
      name: file.original_name || urlName(file.file) || `Файл ${file.id}`,
      mime: file.mime_type || '', size: file.size, version: file.date_create || file.id});
  }
  return nodes;
}

function message(error) {
  const detail = error?.response?.data?.detail;
  return typeof detail === 'string' ? detail
    : error?.message || 'Не удалось открыть файл. Повторите попытку.';
}

export default function ApplicationAttachments({order}) {
  const {account} = useContext(LayoutContext);
  const token = localStorage.getItem('client_token');
  const scope = `${order.id}:${account?.id || ''}:${token || ''}`;
  const nodes = useMemo(() => attachmentNodes(order),
    [order.plan_file, order.plan_file_name, order.plan_file_mime, order.date_update, order.input_files]);
  const [viewer, setViewer] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');
  const requestRef = useRef(null);
  const scopeRef = useRef(scope);
  scopeRef.current = scope;

  useEffect(() => {
    setViewer(null); setBusy(null); setError('');
    return () => requestRef.current?.abort();
  }, [scope]);

  const openViewer = useCallback((node, confirmed) => {
    const images = nodes.filter(item => item.id === node.id || rasterImage(item))
      .map(item => item.id === node.id && confirmed
        ? {...item, name: confirmed.name || item.name, mime: confirmed.mime || item.mime} : item);
    setViewer({images, startId: node.id, scope});
  }, [nodes, scope]);

  const resolveSource = useCallback(async (node, signal) => {
    const result = await applicationAttachmentAccess(order.id, node, true, signal);
    if (!result.preview) throw new Error('Этот формат доступен только для скачивания.');
    return result.url;
  }, [order.id]);

  const downloadFile = useCallback(async (node, signal) => {
    const result = await applicationAttachmentAccess(order.id, node, false, signal);
    downloadApplicationAttachment(result.url);
  }, [order.id]);

  const open = async (event, node) => {
    event.stopPropagation();
    requestRef.current?.abort();
    setError(''); setBusy(null);
    if (rasterImage(node)) { openViewer(node); return; }
    const controller = new AbortController();
    requestRef.current = controller;
    setBusy(node.id);
    const openingScope = scope;
    try {
      // Older plan records can lack metadata. The authorized broker classifies
      // their current file, rather than relying on an expired opaque URL.
      const result = await applicationAttachmentAccess(order.id, node, true, controller.signal);
      if (controller.signal.aborted || scopeRef.current !== openingScope) return;
      if (result.preview) openViewer(node, result);
      else downloadApplicationAttachment(result.url);
    } catch (reason) {
      if (!controller.signal.aborted && scopeRef.current === openingScope && reason.name !== 'AbortError') setError(String(message(reason)));
    } finally {
      if (!controller.signal.aborted && scopeRef.current === openingScope) setBusy(null);
    }
  };

  const plan = nodes.find(node => node.plan);
  const files = nodes.filter(node => !node.plan);
  if (!nodes.length) return null;
  return <Box onClick={event => event.stopPropagation()}>
    {plan && <Button variant="soft" color="primary" onClick={event => open(event, plan)}
      loading={busy === plan.id} startDecorator={<AttachFileRounded/>} sx={{mt: 2, borderRadius: '10px'}}>
      Открыть план помещения
    </Button>}
    {files.length > 0 && <Box sx={{mt: 2}}>
      <Stack direction="row" alignItems="center" spacing={0.6} sx={{mb: 1, color: 'text.tertiary'}}>
        <AttachFileRounded sx={{fontSize: 14}}/>
        <Typography level="body-xs" fontWeight={700} sx={{textTransform: 'uppercase', letterSpacing: '0.06em'}}>Прикреплённые файлы</Typography>
      </Stack>
      <Stack spacing={0.75}>
        {files.map(node => <Button key={node.id} variant="soft" color="neutral" onClick={event => open(event, node)}
          startDecorator={busy === node.id ? <CircularProgress size="sm"/> : <AttachFileRounded sx={{fontSize: 16}}/>}
          sx={{justifyContent: 'flex-start', textAlign: 'left', px: 1.5, py: 0.75, borderRadius: '10px', fontSize: 13, minWidth: 0}}>
          <Box component="span" sx={{overflowWrap: 'anywhere', minWidth: 0}}>{node.name}</Box>
          {Number(node.size) > 0 && <Typography level="body-xs" sx={{ml: 'auto', pl: 1, whiteSpace: 'nowrap'}}>{(node.size / 1024 / 1024).toFixed(1)} МБ</Typography>}
        </Button>)}
      </Stack>
    </Box>}
    {error && <Typography role="alert" color="danger" level="body-sm" sx={{mt: 1}}>{error}</Typography>}
    {viewer?.scope === scope && <ImageViewer images={viewer.images} startId={viewer.startId}
      onClose={() => setViewer(null)} resolveSource={resolveSource} downloadFile={downloadFile}/>}
  </Box>;
}
